import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMic, FiMessageSquare, FiSend, FiX, FiMaximize2 } from 'react-icons/fi';
import { useSector } from '../../contexts/SectorContext';
import {
  runRegisterCommand,
  transcribeAudio,
  RegisterCommandEffect,
} from '../services/MendsVisionService';
import { buildRegisterScreenContext } from '../openRegister';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { pickRecorderMime } from '../../utils/pickRecorderMime';
import { RegisterRelatoFlow } from './RegisterRelatoFlow';
import '../styles/mendsAi.css';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultContactId?: number | null;
  defaultContactName?: string;
  sourceSurface?: string;
  opportunityId?: string | number;
  screenContext?: {
    screen?: string;
    pathname?: string;
    conversationId?: number;
    messageId?: number;
    calendarEventId?: string;
    subjectKind?: 'qualified_contact';
    contactId?: number;
    contactName?: string;
  };
}

type Mode = 'voice' | 'chat' | 'relato';
type Phase = 'idle' | 'listening' | 'thinking' | 'speaking';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  effects?: RegisterCommandEffect[];
}

const MAX_VOICE_MS = 90_000;
const HINTS = [
  'Acabei de falar com João. Pediu 10% e quer retorno sexta.',
  'Essa reunião aconteceu agora. Cliente quer proposta amanhã.',
  'Transfere essa conversa para o Financeiro com o contexto.',
  'Registra que liguei pra Maria — ela pediu retorno amanhã 10h.',
  'Mova o João pra coluna Proposta — ele pediu orçamento.',
];

function speakPt(text: string, onEnd?: () => void) {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text.trim()) {
    onEnd?.();
    return;
  }
  let settled = false;
  let timer = 0;
  const finish = () => {
    if (settled) return;
    settled = true;
    if (timer) window.clearInterval(timer);
    onEnd?.();
  };
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'pt-BR';
    u.rate = 1.04;
    u.onend = finish;
    u.onerror = finish;
    const started = Date.now();
    let sawSpeaking = false;
    const limit = Math.min(45_000, 1800 + text.length * 85);
    window.speechSynthesis.speak(u);
    timer = window.setInterval(() => {
      const synth = window.speechSynthesis;
      if (!synth) {
        finish();
        return;
      }
      if (synth.speaking || synth.pending) sawSpeaking = true;
      const elapsed = Date.now() - started;
      const quiet = !synth.speaking && !synth.pending;
      if (sawSpeaking && quiet) {
        finish();
        return;
      }
      if (!sawSpeaking && quiet && elapsed > 700) {
        finish();
        return;
      }
      if (elapsed > limit) {
        try { synth.cancel(); } catch { /* ignore */ }
        finish();
      }
    }, 200);
  } catch {
    finish();
  }
}

export const RegisterConversationModal: React.FC<Props> = ({
  open,
  onClose,
  defaultContactId,
  defaultContactName,
  sourceSurface,
  opportunityId,
  screenContext,
}) => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  // Relato (voz ou texto → registro com status real) é o padrão; Voz/Chat são o modo de comandos.
  const [mode, setMode] = useState<Mode>('relato');
  const [phase, setPhase] = useState<Phase>('idle');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const stopTimer = useRef<number | null>(null);
  const recordingRef = useRef(false);
  const sessionRef = useRef(0);
  const phaseRef = useRef<Phase>('idle');
  const modeRef = useRef<Mode>('chat');
  const openRef = useRef(false);
  const speakToken = useRef(0);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef<HTMLTextAreaElement | null>(null);

  const setPhaseSafe = (value: Phase) => {
    phaseRef.current = value;
    setPhase(value);
  };

  const setRecordingState = (value: boolean) => {
    recordingRef.current = value;
    setPhaseSafe(value ? 'listening' : 'idle');
  };

  const stopRecording = useCallback(() => {
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
  }, []);

  const runCommand = useCallback(async (raw: string, history: Turn[], opts?: { alreadyAppendedUser?: boolean }) => {
    if (!selectedSectorId) {
      setError('Selecione um setor/conexão primeiro.');
      return;
    }
    const body = raw.trim();
    if (!body) {
      setError('Não ouvi nada. Toque no microfone e fale de novo.');
      return;
    }
    const session = sessionRef.current;
    setPhaseSafe('thinking');
    setError(null);
    let speaking = false;
    const baseHistory = [...history, { role: 'user' as const, content: body }];
    if (!opts?.alreadyAppendedUser) {
      setTurns(baseHistory);
    }
    try {
      const res = await runRegisterCommand({
        sectorId: Number(selectedSectorId),
        contactId: defaultContactId,
        contactNameHint: defaultContactName || undefined,
        text: body,
        history: history.slice(-8).map((t) => ({ role: t.role, content: t.content })),
        screenContext: buildRegisterScreenContext({
          ...screenContext,
          contactId: defaultContactId ?? undefined,
          contactName: defaultContactName || undefined,
        }),
      });
      if (session !== sessionRef.current) return;
      const reply = (res.reply || '').trim() || 'Pronto.';
      const next: Turn[] = [
        ...baseHistory,
        { role: 'assistant', content: reply, effects: res.executed },
      ];
      setTurns(next);
      const afterSpeech = (done: () => void) => {
        speaking = true;
        setPhaseSafe('speaking');
        const token = speakToken.current;
        speakPt(reply, () => {
          if (token !== speakToken.current || session !== sessionRef.current) return;
          setPhaseSafe('idle');
          done();
        });
      };
      if (res.navigateTo) {
        const path = res.navigateTo;
        afterSpeech(() => {
          if (!openRef.current) return;
          onClose();
          navigate(path);
        });
        return;
      }
      if (modeRef.current === 'voice') {
        afterSpeech(() => {});
      }
    } catch (e: unknown) {
      if (session !== sessionRef.current) return;
      setError(apiErrorMessage(e, 'Não consegui executar o comando.'));
    } finally {
      if (!speaking && session === sessionRef.current) setPhaseSafe('idle');
    }
  }, [selectedSectorId, defaultContactId, defaultContactName, navigate, onClose, screenContext]);

  const sendChat = () => {
    const body = draft.trim();
    if (!body || phase === 'thinking' || phase === 'speaking') return;
    setDraft('');
    const nextTurns = [...turns, { role: 'user' as const, content: body }];
    setTurns(nextTurns);
    void runCommand(body, turns, { alreadyAppendedUser: true });
  };

  const expandToMind = () => {
    const q = turns.length
      ? turns.map((t) => `${t.role === 'user' ? 'Eu' : 'Mends'}: ${t.content}`).join('\n').slice(0, 1200)
      : draft.trim();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    close();
    navigate(params.toString() ? `/mente?${params}` : '/mente');
  };

  const startRecording = useCallback(async () => {
    if (recordingRef.current || phaseRef.current === 'thinking' || phaseRef.current === 'speaking') return;
    if (!selectedSectorId) {
      setError('Selecione uma conexão primeiro.');
      return;
    }
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não grava áudio. Use o modo Chat.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const picked = pickRecorderMime();
      const rec = new MediaRecorder(stream, picked.mime ? { mimeType: picked.mime } : undefined);
      chunksRef.current = [];
      startedAt.current = Date.now();
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (stopTimer.current) {
          window.clearTimeout(stopTimer.current);
          stopTimer.current = null;
        }
        const durationMs = Date.now() - startedAt.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || picked.mime || 'audio/webm' });
        setRecordingState(false);
        if (blob.size < 200 || durationMs < 400) {
          setError('Muito curto — toque e fale de novo.');
          return;
        }
        const session = sessionRef.current;
        setPhaseSafe('thinking');
        try {
          const result = await transcribeAudio(
            Number(selectedSectorId),
            blob,
            `voice.${picked.ext}`,
            durationMs,
          );
          if (session !== sessionRef.current) return;
          const transcript = (result.text || '').trim();
          if (!transcript) {
            setError('Não ouvi nada. Toque e fale de novo.');
            setPhaseSafe('idle');
            return;
          }
          await runCommand(transcript, turnsRef.current);
        } catch (e: unknown) {
          if (session !== sessionRef.current) return;
          setError(apiErrorMessage(e, 'Falha no áudio.'));
          setPhaseSafe('idle');
        }
      };
      rec.onerror = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecordingState(false);
        setError('Falha no microfone.');
      };
      mediaRef.current = rec;
      rec.start(250);
      setRecordingState(true);
      setError(null);
      stopTimer.current = window.setTimeout(() => {
        if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
      }, MAX_VOICE_MS);
    } catch {
      setError('Autorize o microfone nas configurações do navegador, ou use o Chat.');
    }
  }, [selectedSectorId, runCommand]);

  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  const onMicTap = () => {
    if (phase === 'thinking' || phase === 'speaking') return;
    if (recordingRef.current) {
      stopRecording();
      return;
    }
    void startRecording();
  };

  const switchMode = (next: Mode) => {
    modeRef.current = next === 'relato' ? 'chat' : next;
    setMode(next);
    speakToken.current += 1;
    stopRecording();
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    if (phaseRef.current === 'speaking' || phaseRef.current === 'listening') setPhaseSafe('idle');
    setError(null);
    if (next === 'chat' || next === 'relato') {
      window.setTimeout(() => draftRef.current?.focus(), 80);
    }
  };

  const close = () => {
    sessionRef.current += 1;
    speakToken.current += 1;
    openRef.current = false;
    stopRecording();
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setTurns([]);
    setDraft('');
    setError(null);
    setPhaseSafe('idle');
    setMode('relato');
    modeRef.current = 'chat';
    onClose();
  };

  useEffect(() => {
    openRef.current = open;
    if (!open) return;
    sessionRef.current += 1;
    setTurns([]);
    setDraft('');
    setError(null);
    setMode('relato');
    modeRef.current = 'chat';
    setPhaseSafe('idle');
    // Idle on open — user taps the mic to start (press-to-talk).
    return () => {
      speakToken.current += 1;
      if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    };
  }, [open]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' });
  }, [turns, mode]);

  if (!open) return null;

  const lastAssistant = [...turns].reverse().find((t) => t.role === 'assistant');
  const listening = phase === 'listening';
  const thinking = phase === 'thinking';
  const speaking = phase === 'speaking';

  return (
    <div className="mai-modal-backdrop" onClick={listening ? undefined : close}>
      <div
        className={`mai-modal mai-reg ${mode === 'voice' ? 'is-voice' : 'is-chat'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mai-reg-top">
          <div className="mai-reg-brand">
            <span className="mai-reg-title">Registrar</span>
            <span className="mai-reg-sub">
              {mode === 'voice'
                ? 'Conte o que aconteceu por voz'
                : mode === 'relato'
                  ? 'Relato → interpretação → ações (com esclarecimento se ambíguo)'
                  : 'Comandos rápidos no contexto da tela (≠ Barra da IA)'}
            </span>
          </div>
          <div className="mai-reg-top-actions">
            <button
              type="button"
              className="mai-reg-expand"
              onClick={expandToMind}
              title="Expandir no Mends AI"
              aria-label="Expandir conversa no Mends AI"
            >
              <FiMaximize2 size={16} />
            </button>
            <button type="button" className="mai-reg-close" onClick={close} aria-label="Fechar">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Registrar unificado: relato e comando (voz ou texto) na mesma entrada; os modos antigos de comando saíram. */}

        {mode === 'relato' ? (
          <>
            <RegisterRelatoFlow
              compact
              defaultContactId={defaultContactId}
              defaultContactName={defaultContactName}
              sourceSurface={sourceSurface || screenContext?.screen}
              conversationId={screenContext?.conversationId}
              opportunityId={opportunityId}
              screenContext={screenContext}
            />
            <button
              type="button"
              className="mai-reg-expand-banner"
              onClick={() => {
                close();
                const q = new URLSearchParams();
                if (defaultContactId) q.set('contactId', String(defaultContactId));
                if (defaultContactName) q.set('contactName', defaultContactName);
                if (sourceSurface) q.set('sourceSurface', String(sourceSurface));
                if (screenContext?.conversationId) {
                  q.set('conversationId', String(screenContext.conversationId));
                }
                navigate(q.toString() ? `/registrar?${q}` : '/registrar');
              }}
            >
              <FiMaximize2 size={14} /> Abrir Registrar em tela cheia
            </button>
          </>
        ) : mode === 'voice' ? (
          <div className="mai-reg-voice">
            <button
              type="button"
              className={`mai-reg-orb ${listening ? 'is-listen' : ''} ${thinking || speaking ? 'is-think' : ''}`}
              onClick={onMicTap}
              disabled={thinking || speaking}
              aria-label={listening ? 'Parar de escutar' : 'Começar a escutar'}
            >
              <FiMic size={28} />
              <span className="mai-reg-bars" aria-hidden>
                <i /><i /><i />
              </span>
            </button>
            <p className="mai-reg-status">
              {thinking ? 'Executando…' : speaking ? 'Falando…' : listening ? 'Ouvindo — toque para parar' : 'Toque para falar'}
            </p>
            {lastAssistant && (
              <p className="mai-reg-reply">{lastAssistant.content}</p>
            )}
            {lastAssistant?.effects && lastAssistant.effects.length > 0 && (
              <div className="mai-reg-effects">
                {lastAssistant.effects.map((fx, i) => (
                  <span key={`${fx.tool}-${i}`} className={fx.ok ? 'is-ok' : 'is-err'}>
                    {fx.summary}
                  </span>
                ))}
              </div>
            )}
            {!lastAssistant && !thinking && (
              <div className="mai-reg-hints">
                {HINTS.map((h) => (
                  <span key={h}>{h}</span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mai-reg-chat">
            <div className="mai-reg-thread">
              {turns.length === 0 && (
                <div className="mai-reg-empty">
                  <p>Digite um comando. Exemplos:</p>
                  <ul>
                    {HINTS.slice(0, 4).map((h) => (
                      <li key={h}>
                        <button type="button" onClick={() => setDraft(h.replace('…', ''))}>{h}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {turns.map((t, i) => (
                <div key={i} className={`mai-reg-bubble is-${t.role}`}>
                  {t.content}
                  {t.role === 'assistant' && t.effects && t.effects.length > 0 && (
                    <div className="mai-reg-effects">
                      {t.effects.map((fx, j) => (
                        <span key={`${fx.tool}-${j}`} className={fx.ok ? 'is-ok' : 'is-err'}>
                          {fx.summary}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {thinking && (
                <div className="mai-reg-bubble is-assistant is-pending" aria-live="polite">
                  Pensando…
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {turns.length >= 4 && (
              <button type="button" className="mai-reg-expand-banner" onClick={expandToMind}>
                <FiMaximize2 size={14} /> Continuar no Mends AI (tela cheia)
              </button>
            )}
            <form
              className="mai-reg-composer"
              onSubmit={(e) => { e.preventDefault(); sendChat(); }}
            >
              <textarea
                ref={draftRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                placeholder="Ex.: gere uma mensagem para o João oferecendo o plano X…"
                disabled={thinking}
                rows={3}
                autoFocus
              />
              <button type="submit" disabled={thinking || !draft.trim()} aria-label="Enviar">
                <FiSend size={18} />
              </button>
            </form>
          </div>
        )}

        {error && <p className="mai-form-error">{error}</p>}
      </div>
    </div>
  );
};
